import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useCategoryItemType } from './useCategoryItemType';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import CategoryItemTypeDetails from './CategoryItemTypeDetails';

const CategoryItemType = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const { category_item_type, isLoading, error, mutate } = useCategoryItemType(searchQuery);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCategoryItemType, setSelectedCategoryItemType] = useState(null);
  const NAV_URL = `category_item_type`;
  const URL = `production-category-item-type`;

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  const handleClear = () => {
    setSearchQuery("");
    mutate(); // Refetch data after clearing search
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedCategoryItemType(null);
  };

  const handleView = (category_item_type) => {
    setSelectedCategoryItemType(category_item_type);
  };

  const handleEdit = (id) => {
    navigate(`/${NAV_URL}/update/${id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_URL}/create/`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Επιβεβαίωση διαγραφής',
      message: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτήν την εγγραφή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Η εγγραφή διαγράφηκε με επιτυχία.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Αποτυχία διαγραφής εγγραφής. Σφάλμα: " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'Όχι',
        }
      ]
    });
  }, [mutate, notifier]);


  const columnsToExtract = ['clientName', 'clientName', 'contactPerson', 'clientAddress', 'clientCity', 'clientPhone', 'clientEmail', 'clientNotes'];

  const handleExportToExcel = () => {
    exportToExcel(category_item_type || [], columns, columnsToExtract, 'clients-list', 'Clients List');
  };

  const handleExportToPdf = () => {
    exportToPdf(category_item_type || [], columns, columnsToExtract, 'clients-list');
  };

  const handleExportToCsv = () => {
    exportToCsv(category_item_type || [], columns, columnsToExtract, 'clients-list');
  };

  const columns = [
    { field: 'pcit_descr', headerName: 'Περιγραφή', flex: 1, headerAlign: 'center', },
    {
      field: 'pcit_aa', headerName: 'A/A', flex: 1, headerAlign: 'center', renderCell: (params) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Actions",
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem color="primary" icon={<EditOutlinedIcon />} label="Edit" onClick={() => handleEdit(params.row.id)} />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Category Item Type" module="category_item_type" subtitle="List of category item types" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItemType ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            disableExport={true}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>
              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />
              {isLoading && <LinearProgress />}
              {error && <ErrorNotification ml={2} mr={2} error={error} />}
              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={category_item_type || []}
                  columns={columns}
                  getRowId={getRowId}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedCategoryItemType(params.row)} // Set selected client on row click
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {selectedCategoryItemType && (
          <Grid item xs={4}>
            <CategoryItemTypeDetails
              category_item_type={selectedCategoryItemType}
              onClose={handlePanelClose} />

          </Grid>
        )}
      </Grid>
    </Box>

  );
};

export default CategoryItemType;